import { Loader2 } from 'lucide-react'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'yellow'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'kiosk'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  fullWidth?: boolean
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-momo-red text-momo-white hover:bg-momo-red-dark active:bg-momo-red-dark shadow-glow-red',
  secondary:
    'bg-surface-raised text-content border border-surface-border hover:border-surface-border-strong hover:bg-surface-overlay',
  ghost: 'bg-transparent text-content-secondary hover:bg-surface-raised hover:text-content',
  danger: 'bg-danger text-momo-white hover:brightness-110',
  yellow:
    'bg-momo-yellow text-momo-black font-semibold hover:bg-momo-yellow-dark shadow-glow-yellow',
}

/** Sizes honour the NFR-8 touch minimum; `kiosk` is bigger again (PRD §6). */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-10 px-4 text-sm rounded-md gap-2',
  md: 'h-12 px-6 text-base rounded-lg gap-2',
  lg: 'h-14 px-8 text-lg rounded-xl gap-3',
  kiosk: 'h-16 px-10 text-xl rounded-2xl gap-3',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // A loading button must not fire again on a second tap (FR-ORD-6).
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium transition-all duration-150',
        'focus-visible:outline-2 focus-visible:outline-momo-yellow focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none',
        'active:scale-[0.98] touch-surface',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading && <Loader2 aria-hidden="true" className="size-5 animate-spin" />}
      {children}
    </button>
  )
})
